import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import SoundPlayer from "@/components/SoundPlayer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronDown, ChevronUp, AlertTriangle, Shield, ArrowRight, Headphones, Clock, Repeat, Lightbulb } from "lucide-react";
import { setCanonical } from "@/utils/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConditionPage() {
  const { slug } = useParams();
  const [condition, setCondition] = useState(null);
  const [related, setRelated] = useState([]);
  const [solfeggioInfo, setSolfeggioInfo] = useState(null);
  const [brainwaveInfo, setBrainwaveInfo] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    axios.get(`${API}/conditions/${slug}`).then(r => {
      setCondition(r.data.condition);
      setRelated(r.data.related);
      setSolfeggioInfo(r.data.solfeggio_info);
      setBrainwaveInfo(r.data.brainwave_info);
      setLoading(false);
      document.title = `Sound Therapy for ${r.data.condition.name} | Binaural Beats & Solfeggio | ToneHealing`;
      setCanonical(`/therapy/${slug}`);
    }).catch(() => setLoading(false));
    axios.get(`${API}/banner/active`).then(r => setBanner(r.data.banner)).catch(() => {});
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center pt-16">
      <div className="animate-pulse text-muted-foreground">Loading therapy...</div>
    </div>
  );

  if (!condition) return (
    <div className="min-h-screen flex flex-col items-center justify-center pt-16 gap-4">
      <p className="text-muted-foreground">Condition not found</p>
      <Link to="/"><Button variant="outline">Back to Home</Button></Link>
    </div>
  );

  const brainwaveColorClass = {
    Delta: "text-violet-400", Theta: "text-primary", Alpha: "text-accent", Beta: "text-orange-400"
  }[condition.brainwave_type] || "text-primary";

  const brainwaveBgClass = {
    Delta: "bg-violet-400/10 border-violet-400/20",
    Theta: "bg-primary/10 border-primary/20",
    Alpha: "bg-accent/10 border-accent/20",
    Beta: "bg-orange-400/10 border-orange-400/20",
  }[condition.brainwave_type] || "bg-primary/10 border-primary/20";

  const generateFaqs = () => [
    { q: `How do binaural beats help with ${condition.name.toLowerCase()}?`, a: `Binaural beats at ${condition.frequency}Hz create ${condition.brainwave_type} wave entrainment in your brain. ${condition.description} When you listen with headphones, your brain synchronizes to this frequency, naturally shifting into a ${condition.brainwave_type.toLowerCase()} state that directly addresses ${condition.name.toLowerCase()}.` },
    { q: `How long should I listen to sound therapy for ${condition.name.toLowerCase()}?`, a: `For ${condition.name.toLowerCase()}, we recommend starting with 10-15 minute sessions. ${condition.brainwave_type === "Delta" ? "Since Delta waves promote deep states, you may find 20-30 minute sessions especially beneficial before sleep." : condition.brainwave_type === "Beta" ? "Beta wave sessions can be shorter (10-15 minutes) as they are stimulating. Use them when you need a quick boost." : "Gradually increase to 20-30 minutes as you become accustomed to the frequencies."} Consistency is more important than duration — daily sessions produce the best results.` },
    { q: `Do I need headphones for ${condition.name.toLowerCase()} therapy?`, a: `Yes, stereo headphones are required for binaural beats to work. The therapy relies on delivering ${condition.base_frequency}Hz to one ear and ${condition.base_frequency + condition.frequency}Hz to the other ear. Your brain perceives the ${condition.frequency}Hz difference, creating ${condition.brainwave_type} wave entrainment. Any stereo headphones or earbuds will work.` },
    { q: `What is the ${condition.solfeggio}Hz Solfeggio frequency used for?`, a: solfeggioInfo ? `The ${condition.solfeggio}Hz Solfeggio frequency is known as the "${solfeggioInfo.name}" frequency. ${solfeggioInfo.description}. When combined with ${condition.brainwave_type} wave binaural beats, it creates a powerful multi-layered therapy that addresses ${condition.name.toLowerCase()} from both a brainwave and cellular level.` : `The ${condition.solfeggio}Hz Solfeggio frequency adds an additional layer of healing to the binaural beats therapy, enhancing the overall therapeutic effect for ${condition.name.toLowerCase()}.` },
  ];

  const faqs = generateFaqs();

  const benefits = [
    `Targets ${condition.brainwave_type} brainwave state at ${condition.frequency}Hz for ${condition.name.toLowerCase()}`,
    `Includes ${condition.solfeggio}Hz Solfeggio frequency${solfeggioInfo ? ` for ${solfeggioInfo.description.toLowerCase()}` : ''}`,
    "No side effects when used as directed — completely non-invasive",
    "Free to use — no subscription, no account required",
    `Customizable sessions with volume control, duration, and ambient sounds`,
    "Science-backed brainwave entrainment technology",
  ];

  const tips = [
    { icon: Headphones, text: "Use headphones or earbuds — essential for binaural beats" },
    { icon: Shield, text: "Find a quiet, comfortable space — minimize distractions" },
    { icon: Clock, text: `Start with ${condition.brainwave_type === "Beta" ? "10" : "15"}-minute sessions, increase gradually` },
    { icon: Repeat, text: "Regular daily sessions produce the best results" },
    { icon: Lightbulb, text: "Enable Solfeggio frequencies for enhanced wellness" },
  ];

  return (
    <div className="relative" data-testid="condition-page">
      {/* Hero */}
      <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-20 overflow-hidden" style={{ backgroundImage: "url('/hero-bg-2.png')", backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background" />
        <div className={`absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full blur-3xl animate-healing-pulse opacity-10 ${
          condition.brainwave_type === "Delta" ? "bg-violet-500" :
          condition.brainwave_type === "Theta" ? "bg-teal-400" :
          condition.brainwave_type === "Alpha" ? "bg-amber-400" : "bg-orange-400"
        }`} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <Link to="/" className="inline-flex items-center gap-2 text-base font-semibold text-primary hover:text-primary/80 mb-6 transition-colors bg-white/60 px-4 py-2 rounded-full" data-testid="back-link">
                <ArrowLeft className="w-5 h-5" /> Back to All Conditions
              </Link>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Badge variant="outline" className={`border ${brainwaveBgClass}`}>
                  <span className={brainwaveColorClass}>{condition.brainwave_type} Waves</span>
                </Badge>
                <Badge variant="outline" className="border-border/30">
                  <span className="font-mono-freq">{condition.frequency}Hz</span>
                </Badge>
                <Badge variant="outline" className="border-border/30">{condition.category}</Badge>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4" data-testid="condition-title">
                Sound Therapy for <span className={brainwaveColorClass}>{condition.name}</span>
                <img src="/hero-meditation.png" alt={`Sound therapy meditation for ${condition.name}`} className="inline-block w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover ml-2 align-middle lg:hidden" />
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">{condition.description}</p>
              {/* GEO: Definition-lead sentence for AI engine citation */}
              <p className="speakable-intro text-xs text-muted-foreground/60 mt-3 max-w-2xl">
                Sound therapy for {condition.name} is a science-backed wellness practice that uses {condition.brainwave_type} wave binaural beats at {condition.frequency}Hz to naturally support {condition.name.toLowerCase()} relief — free, no account required.
              </p>
            </div>

            {/* Meditation image in bubble */}
            <div className="hidden lg:flex items-center justify-center relative">
              <div className="absolute w-56 h-56 rounded-full border-2 border-primary/20" style={{ animation: 'ring-pulse-1 3s ease-in-out infinite' }} />
              <div className="absolute w-72 h-72 rounded-full border border-primary/10" style={{ animation: 'ring-pulse-2 4s ease-in-out infinite 0.5s' }} />
              <img
                src="/hero-meditation.png"
                alt={`Sound therapy meditation for ${condition.name}`}
                className="relative z-10 w-52 h-52 object-cover rounded-full drop-shadow-2xl animate-wave-float"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Sound Player */}
            <div className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start order-1 lg:order-2">
              <SoundPlayer condition={condition} />
            </div>

            {/* Content */}
            <div className="lg:col-span-5 space-y-12 order-2 lg:order-1">
              {/* How it works */}
              <div>
                <h2 className="text-2xl font-bold mb-4">How {condition.brainwave_type} Wave Therapy Helps</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  When you listen to our {condition.name.toLowerCase()} therapy session, your brain receives two slightly different frequencies through your headphones: {condition.base_frequency}Hz in your left ear and {condition.base_frequency + condition.frequency}Hz in your right ear. Your brain perceives the {condition.frequency}Hz difference as a gentle rhythmic pulse, naturally synchronizing your brainwaves to the {condition.brainwave_type} frequency range ({brainwaveInfo?.range}).
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  {condition.brainwave_type} waves are associated with {brainwaveInfo?.state?.toLowerCase()}. This brainwave state has been shown in research to support people dealing with {condition.name.toLowerCase()}. When combined with the {condition.solfeggio}Hz Solfeggio frequency{solfeggioInfo ? ` (${solfeggioInfo.name} — ${solfeggioInfo.description.toLowerCase()})` : ''}, the therapy creates a comprehensive, multi-layered wellness experience.
                </p>
              </div>

              {/* Benefits */}
              <div>
                <h2 className="text-2xl font-bold mb-4">Benefits</h2>
                <ul className="space-y-3">
                  {benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${brainwaveColorClass.replace('text-', 'bg-')}`} />
                      <span className="text-sm text-muted-foreground">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Best Results Tips */}
              <div>
                <h2 className="text-2xl font-bold mb-4">Tips for Best Results</h2>
                <div className="space-y-3">
                  {tips.map((tip, i) => (
                    <div key={i} className="flex items-center gap-3 glass-light rounded-xl p-4">
                      <tip.icon className="w-5 h-5 text-primary shrink-0" />
                      <span className="text-sm text-muted-foreground">{tip.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQ */}
              <div data-testid="condition-faq">
                <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
                <div className="space-y-3">
                  {faqs.map((faq, i) => (
                    <div key={i} className="glass-light rounded-xl overflow-hidden">
                      <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left" data-testid={`condition-faq-${i}`}>
                        <span className="text-sm font-medium pr-4">{faq.q}</span>
                        {expandedFaq === i ? <ChevronUp className="w-4 h-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />}
                      </button>
                      {expandedFaq === i && (
                        <div className="px-5 pb-5">
                          <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="lg:col-span-3 space-y-6 order-3">
              <div className="lg:sticky lg:top-24">
                <div className="ad-sidebar p-3 text-center mb-6" data-testid="condition-sidebar-banner">
                  {banner && banner.active ? (
                    <a href={banner.click_url || "#"} target="_blank" rel="noopener noreferrer">
                      {banner.image_url ? <img src={banner.image_url} alt={banner.alt_text || "Sponsored"} className="w-full rounded-lg" /> :
                       <div className="bg-secondary/50 rounded-lg p-6"><p className="text-xs text-muted-foreground/50">Sponsor Space</p></div>}
                      <p className="text-[9px] text-muted-foreground/30 mt-1">Sponsored</p>
                    </a>
                  ) : (
                    <div className="bg-secondary/50 rounded-lg p-6 min-h-[200px] flex flex-col items-center justify-center">
                      <p className="text-xs text-muted-foreground/50 font-medium">Your Ad Here</p>
                      <p className="text-[10px] text-muted-foreground/30 mt-1">support@lifeownit.com</p>
                    </div>
                  )}
                </div>

                {related.length > 0 && (
                  <div className="glass rounded-xl p-4">
                    <h4 className="text-sm font-semibold mb-3">Related Therapies</h4>
                    <div className="space-y-2">
                      {related.map(r => (
                        <Link key={r.slug} to={`/therapy/${r.slug}`} className="block text-sm text-primary hover:underline">
                          {r.name} <span className="text-muted-foreground font-mono-freq text-xs">{r.frequency}Hz</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-3xl mx-auto px-4">
          <div className="rounded-2xl p-6 bg-destructive/5 border border-destructive/20">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-destructive" />
              <h3 className="text-sm font-bold text-destructive">Important Safety Warning</h3>
            </div>
            <p className="text-xs text-destructive/80 leading-relaxed">
              DO NOT use sound therapy while driving, operating machinery, or during any activity requiring alertness. 
              These frequencies alter brainwave patterns and may cause drowsiness or altered states of consciousness. 
              Use only in a safe, stationary environment. Not recommended for epilepsy or seizure disorders.
            </p>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16" data-testid="related-conditions">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold mb-6">Related Conditions in {condition.category}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map(r => (
                <Link key={r.slug} to={`/therapy/${r.slug}`} className="condition-card glass-light rounded-xl p-5 group" data-testid={`related-${r.slug}`}>
                  <div className="font-medium text-sm mb-1 group-hover:text-primary transition-colors">{r.name}</div>
                  <div className="font-mono-freq text-xs text-muted-foreground">{r.brainwave_type} &middot; {r.frequency}Hz</div>
                  <ArrowRight className="w-3 h-3 text-muted-foreground mt-2 group-hover:text-primary transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-8" data-testid="condition-bottom-adsense">
        <div className="max-w-5xl mx-auto px-4">
          <div className="rounded-2xl border border-dashed border-primary/20 bg-white/60 p-8 text-center min-h-[120px] flex flex-col items-center justify-center">
            <p className="text-sm text-muted-foreground/40">Google AdSense</p>
            <p className="text-xs text-muted-foreground/25 mt-1">Ad space — Pending Google approval</p>
          </div>
          <p className="text-center text-[9px] text-muted-foreground/20 mt-2">Advertisement</p>
        </div>
      </section>

      {/* Schema — Google AI Mode + GEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": `Sound Therapy for ${condition.name}`,
        "description": `Sound therapy for ${condition.name} is a science-backed wellness practice using ${condition.brainwave_type} wave binaural beats at ${condition.frequency}Hz. Free, no account required.`,
        "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", ".speakable-intro"] },
        "url": `https://tonehealing.com/therapy/${condition.slug}`
      })}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": `Sound Therapy for ${condition.name}`,
        "description": condition.description,
        "provider": { "@type": "Organization", "name": "ToneHealing", "url": "https://tonehealing.com" },
        "areaServed": "Worldwide",
        "serviceType": "Sound Therapy",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
      })}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://tonehealing.com" },
          { "@type": "ListItem", "position": 2, "name": condition.category, "item": `https://tonehealing.com/#conditions` },
          { "@type": "ListItem", "position": 3, "name": `Sound Therapy for ${condition.name}`, "item": `https://tonehealing.com/therapy/${condition.slug}` },
        ]
      })}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
      })}} />
    </div>
  );
}
